// /devices — Camera Tile Grid（spec 16 §4）。
// 縮圖降級：snapshot API 未落地 → 漸層 placeholder + 型號 icon（16 §4.1 定版規則）。
// 新增攝影機：QR 解析 + owner_token 取得可在 Web 完成；BLE/AP 近場 provisioning
// 仍由手機/裝置本地能力接續（16 §7.3 / APP#30 phase 1）。

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DEGRADED_BANNER_TEXT, useConnectionMode } from '../../lib/connection';
import { useTranslation } from 'react-i18next';
import { Illustration } from '../../components/Illustration';
import { formatRelativeUs } from '../../lib/format';
import { DegradedExplainSheet } from './DegradedExplainSheet';
import type { DeviceSummary } from './deviceModel';
import { batterySummary, capabilityPendingText, recordingModeLabel, cameraClass, deviceIconKind, groupDevicesByClass, deviceName, deviceStateLabel, eventTypeLabel, gradientIndex, subscriptionExpired, tileState, type DeviceIconKind } from './deviceModel';
import { quotaBadge, quotaPercent, type QuotaEntry } from './quotaModel';
import { useDeviceList } from './useDeviceList';
import { useDeviceQuota } from './useDeviceQuota';
import { SnoozeControl } from './SnoozeControl';
import { groupByRoom, loadRooms, saveRoom } from './rooms';
import { loadPins, togglePin, loadSort, saveSort, sortDevices, SORT_MODES, type SortMode, loadDensity, saveDensity, type Density, loadOrder, saveOrder, moveInOrder } from './pinSort';
import { useOptionalAuth } from '../../auth/AuthContext';
import { PairingPanel } from './PairingPanel';
import { BatchActionBar } from './BatchActionBar';
import './devices.css';

export function CameraIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="6" width="14" height="11" rx="2" />
      <path d="m22 8-6 3.5L22 15z" fill="currentColor" stroke="none" />
      <circle cx="9" cy="11.5" r="2.5" />
    </svg>
  );
}

// APP#39：非相機裝置 tile 圖示（插座 / 感測器 / 泛用裝置）。
function PlugIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 2v6M15 2v6" />
      <path d="M6 8h12v3a6 6 0 0 1-12 0z" />
      <path d="M12 17v5" />
    </svg>
  );
}
function SensorIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M10 13.5V5a2 2 0 0 1 4 0v8.5a4 4 0 1 1-4 0z" />
      <circle cx="12" cy="17" r="1.5" fill="currentColor" stroke="none" />
    </svg>
  );
}
function DeviceIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="4" y="4" width="16" height="16" rx="3" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}
function TileIcon({ kind }: { kind: DeviceIconKind }) {
  switch (kind) {
    case 'plug':
      return <PlugIcon />;
    case 'sensor':
      return <SensorIcon />;
    case 'device':
      return <DeviceIcon />;
    default:
      return <CameraIcon />;
  }
}

function DeviceTile({
  d,
  pinned,
  onTogglePin,
  quota,
  onExplain,
  reorderMode = false,
  onReorderDrop,
  selectMode = false,
  selected = false,
  onToggleSelect,
}: {
  d: DeviceSummary;
  pinned: boolean;
  onTogglePin: (id: string) => void;
  quota: QuotaEntry | undefined;
  onExplain: (deviceId: string) => void;
  reorderMode?: boolean;
  onReorderDrop?: (fromId: string, toId: string) => void;
  // APP#51 多選批次：selectMode 下點卡片 = 切換勾選（不導頁）。
  selectMode?: boolean;
  selected?: boolean;
  onToggleSelect?: (id: string) => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const st = tileState(d);
  // W2-02：degraded/offline → 即時觀看入口 disable（spec 10 §4.3）
  const liveDisabled = useConnectionMode() !== 'online';
  // LV 入口 capability 閘（16 §3.2 過濾規則延伸至 Tile ▶，2026-06-12）：
  // non-camera（如 sensor-th 僅 0x8000/0x8010）→ ▶ 整顆不渲染（隱藏優於 disable），
  // 避免點入 start_viewing 30s timeout 白等；unknown = cold-start（21 §2.4）→
  // 不得判為非攝影機，▶ 渲染但 disable +「等待裝置首次連線…」。
  const cc = cameraClass(d);
  const iconKind = deviceIconKind(d); // APP#39：非相機裝置換圖示 + 類別 badge
  const isOwner = d.role === 'owner';
  const expired = isOwner && subscriptionExpired(d);
  // 配額 badge（19 §6.1）：subscription expired 覆蓋條優先，shared device 不顯示 owner-only quota。
  const badge = isOwner && !expired ? quotaBadge(quota) : null;
  const warnPct = badge?.level === 'warning' ? quotaPercent(badge.usageRatio) : null;
  const recMode = d.recording?.mode;
  const recLabel = recMode && recMode !== 'off' ? recordingModeLabel(recMode) : null;
  const powerLabel = batterySummary(d);
  const openDetail = () => navigate(`/devices/${encodeURIComponent(d.device_id)}`);
  // selectMode 優先於 reorderMode：點卡片切換勾選、鍵盤 Enter/Space 同步。
  const primaryAction = selectMode ? () => onToggleSelect?.(d.device_id) : reorderMode ? undefined : openDetail;

  return (
    <article
      className={`dv-tile${st === 'offline' ? ' dv-offline' : ''}${reorderMode ? ' dv-reorder' : ''}${selectMode ? ' dv-selectable' : ''}${selected ? ' dv-selected' : ''}`}
      data-testid={`device-tile-${d.device_id}`}
      role={selectMode ? 'checkbox' : 'button'}
      aria-checked={selectMode ? selected : undefined}
      tabIndex={0}
      draggable={reorderMode && !selectMode}
      onClick={primaryAction}
      onDragStart={reorderMode && !selectMode ? (e) => e.dataTransfer.setData('text/plain', d.device_id) : undefined}
      onDragOver={reorderMode && !selectMode ? (e) => e.preventDefault() : undefined}
      onDrop={
        reorderMode && !selectMode
          ? (e) => {
              e.preventDefault();
              const from = e.dataTransfer.getData('text/plain');
              if (from && from !== d.device_id) onReorderDrop?.(from, d.device_id);
            }
          : undefined
      }
      onKeyDown={(e) => {
        // role=button/checkbox 需補 Space；只在 tile 本體聚焦時觸發，避免內層按鈕冒泡誤觸。
        if (primaryAction && (e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
          e.preventDefault();
          primaryAction();
        }
      }}
    >
      {selectMode && (
        <span className="dv-select-check" aria-hidden="true">
          {selected ? '☑' : '☐'}
        </span>
      )}
      <div className={`dv-thumb dv-grad-${gradientIndex(d)}`}>
        <TileIcon kind={iconKind} />
        <button
          className={`dv-pin${pinned ? ' dv-pinned' : ''}`}
          aria-label={pinned ? t('devices.unpin') : t('devices.pin')}
          aria-pressed={pinned}
          title={pinned ? t('devices.unpin') : t('devices.pin')}
          onClick={(e) => {
            e.stopPropagation();
            onTogglePin(d.device_id);
          }}
        >
          <span aria-hidden="true">{pinned ? '★' : '☆'}</span>
        </button>
        {expired && <div className="dv-sub-banner">{t('devices.cloudRecordingDisabled')}</div>}
        {badge?.level === 'over' && (
          <button
            type="button"
            className="dv-sub-banner dv-degraded"
            onClick={(e) => {
              e.stopPropagation();
              onExplain(d.device_id);
            }}
          >
            {t('devices.cloudRecordingDegraded')}
          </button>
        )}
        {badge?.level === 'warning' && (
          <button
            type="button"
            className="dv-quota-badge"
            onClick={(e) => {
              e.stopPropagation();
              onExplain(d.device_id);
            }}
          >
            {warnPct != null ? t('devices.quotaUsagePercent', { percent: warnPct }) : t('devices.quotaUsageHigh')}
          </button>
        )}
        {st !== 'offline' && cc !== 'non-camera' && (
          <button
            className="dv-play"
            aria-label={t('devices.watchLive')}
            disabled={liveDisabled || cc === 'unknown'}
            title={cc === 'unknown' ? capabilityPendingText() : liveDisabled ? DEGRADED_BANNER_TEXT : undefined}
            onClick={(e) => {
              e.stopPropagation();
              if (liveDisabled || cc === 'unknown') return;
              navigate(`/devices/${encodeURIComponent(d.device_id)}/live`);
            }}
          >
            <span aria-hidden="true">▶</span>
          </button>
        )}
        {/* 韌體可更新:原本只有色點 + title(色彩單一傳達 + 觸控/SR 讀不到)→ 補 role/aria-label（a11y review） */}
        {d.firmware_upgrade?.needs_upgrade && (
          <span
            className="dv-fw-dot"
            role="img"
            aria-label={d.firmware_upgrade.latest_version ? t('devices.firmwareUpgradableTo', { version: d.firmware_upgrade.latest_version }) : t('devices.firmwareUpgradable')}
            title={d.firmware_upgrade.latest_version ? t('devices.firmwareUpgradableTo', { version: d.firmware_upgrade.latest_version }) : t('devices.firmwareUpgradable')}
          />
        )}
      </div>
      <div className="dv-tile-body">
        <div className="dv-tile-title">
          <span className="dv-name">{deviceName(d)}</span>
          {iconKind !== 'camera' && <span className="dv-badge dv-badge-class">{t(`devices.tileBadge_${iconKind}`)}</span>}
          {d.role && d.role !== 'owner' && <span className="dv-badge">{t('devices.shared')}</span>}
        </div>
        <div className="dv-tile-meta">
          <span className={`dv-dot dv-dot-${st}`} />
          <span className="text-secondary">
            {deviceStateLabel(d)}
            {st === 'offline' && d.online?.last_seen != null ? t('devices.lastOnlineSuffix', { time: formatRelativeUs(d.online.last_seen) }) : ''}
          </span>
          {recLabel && <span className="dv-badge dv-badge-rec">{recLabel}</span>}
          {powerLabel && <span className="dv-badge dv-badge-power">{powerLabel}</span>}
        </div>
        {d.last_event?.event_ts != null && (
          <p className="dv-tile-event text-secondary">
            {t('devices.eventWithTime', { event: eventTypeLabel(d.last_event.event_type), time: formatRelativeUs(d.last_event.event_ts) })}
          </p>
        )}
        <div className="dv-tile-actions">
          <SnoozeControl deviceId={d.device_id} compact />
        </div>
      </div>
    </article>
  );
}

export default function DevicesPage() {
  const { t } = useTranslation();
  const { devices, loading, error, reload } = useDeviceList();
  // 配額快照（GET /v3/me/quota，owner-only；端點不可用 → 空 Map → 無 badge，19 §6.1）。
  const { quotaByDevice } = useDeviceQuota();
  const [showAdd, setShowAdd] = useState(false);
  // 房間分組(V4P-5,自 ls-v4 myhome 移植):v1 本地偏好(§9-G3 同模式),
  // 伺服端同步列缺口。
  const uid = useOptionalAuth()?.user?.user_id ?? '';
  const [rooms, setRooms] = useState(() => loadRooms(uid));
  const [editRooms, setEditRooms] = useState(false);
  useEffect(() => setRooms(loadRooms(uid)), [uid]);
  const roomNames = Array.from(new Set(Object.values(rooms))).sort();
  // 釘選 + 排序(APP#27,per-uid 本地偏好,同 rooms 模式)
  const [pins, setPins] = useState(() => loadPins(uid));
  const [sort, setSort] = useState<SortMode>(() => loadSort(uid));
  // 卡片密度 + 手動排序(APP#51,VistaFlow 卡片尺寸 / 拖曳排序對標;per-uid 本地偏好)
  const [density, setDensity] = useState<Density>(() => loadDensity(uid));
  const [order, setOrder] = useState<string[]>(() => loadOrder(uid));
  const [reorderMode, setReorderMode] = useState(false);
  // 多選批次(APP#51 後續子項):selectMode + 勾選集;與 reorder/editRooms 互斥。
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  useEffect(() => {
    setPins(loadPins(uid));
    setSort(loadSort(uid));
    setDensity(loadDensity(uid));
    setOrder(loadOrder(uid));
  }, [uid]);
  const onTogglePin = (id: string) => setPins(togglePin(uid, id));
  const toggleDensity = () => setDensity((d) => saveDensity(uid, d === 'compact' ? 'comfortable' : 'compact'));
  // 拖曳落點:把 fromId 移到 toId 之前,存本地並切到 custom 排序讓結果生效。
  const onReorderDrop = (fromId: string, toId: string) => {
    const present = devices ? devices.map((x) => x.device_id) : [];
    const next = moveInOrder(order, fromId, toId, present);
    setOrder(saveOrder(uid, next));
    if (sort !== 'custom') setSort(saveSort(uid, 'custom'));
  };
  const toggleSelect = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  // 進多選 → 關掉 reorder/editRooms(互斥);離開 → 清勾選。
  const enterSelectMode = () => {
    setReorderMode(false);
    setEditRooms(false);
    setSelectMode(true);
  };
  const exitSelectMode = () => {
    setSelectMode(false);
    setSelected(new Set());
  };
  const [sheetDeviceId, setSheetDeviceId] = useState<string | null>(null);
  const sheetDevice = sheetDeviceId && devices ? devices.find((d) => d.device_id === sheetDeviceId) ?? null : null;

  return (
    <section>
      <header className="dv-page-head">
        <h1>{t('devices.cameras')}</h1>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <label className="dv-sort">
            <span className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>{t('devices.sortBy')}</span>
            <select
              value={sort}
              data-testid="devices-sort"
              onChange={(e) => setSort(saveSort(uid, e.target.value as SortMode))}
            >
              {SORT_MODES.map((m) => (
                <option key={m} value={m}>{t(`devices.sort_${m}`)}</option>
              ))}
            </select>
          </label>
          <button className="btn-ghost" onClick={toggleDensity} data-testid="density-toggle" aria-pressed={density === 'compact'}>
            {density === 'compact' ? t('devices.densityComfortable') : t('devices.densityCompact')}
          </button>
          <button
            className="btn-ghost"
            onClick={() => (selectMode ? exitSelectMode() : enterSelectMode())}
            data-testid="select-toggle"
            aria-pressed={selectMode}
          >
            {selectMode ? t('common.done') : t('devices.multiSelect')}
          </button>
          <button
            className="btn-ghost"
            onClick={() => setReorderMode((v) => !v)}
            data-testid="reorder-toggle"
            aria-pressed={reorderMode}
            disabled={selectMode}
          >
            {reorderMode ? t('common.done') : t('devices.reorder')}
          </button>
          <button className="btn-ghost" onClick={() => setEditRooms((v) => !v)} data-testid="rooms-edit-toggle" disabled={selectMode}>
            {editRooms ? t('common.done') : t('devices.editRooms')}
          </button>
          <button className="btn-ghost" onClick={() => void reload()} disabled={loading}>
            {loading ? t('common.loading') : t('common.refresh')}
          </button>
        </div>
      </header>

      {reorderMode && (
        <p className="text-secondary" role="status" style={{ margin: '0 0 var(--spacing-4)', fontSize: 'var(--font-size-sm)' }}>
          {t('devices.reorderHint')}
        </p>
      )}

      {selectMode && devices && (
        <BatchActionBar
          devices={devices}
          selectedIds={selected}
          uid={uid}
          roomNames={roomNames}
          onExit={exitSelectMode}
          onClearSelection={() => setSelected(new Set())}
          onRoomsChange={setRooms}
          onReload={reload}
        />
      )}

      {loading && !devices && (
        <div className="dv-grid" aria-label={t('common.loading')}>
          {[0, 1, 2].map((i) => (
            <div key={i} className="dv-tile dv-skeleton">
              <div className="dv-thumb" />
              <div className="dv-tile-body">
                <div className="dv-skel-line" />
                <div className="dv-skel-line short" />
              </div>
            </div>
          ))}
        </div>
      )}

      {error && !loading && (
        <div className="card dv-state-card">
          <p className="error-text" style={{ margin: 0 }}>
            {t('devices.listLoadFailed', { error })}
          </p>
          <button className="btn-primary" onClick={() => void reload()}>
            {t('common.retry')}
          </button>
        </div>
      )}

      {!loading && !error && devices && devices.length === 0 && (
        // 00 §7.2 首屏過渡態：0 台 device 不渲染空 Grid，顯示空狀態 + 新增 CTA
        <div className="card dv-state-card">
          <Illustration name="no-devices" alt="" />
          <h2 style={{ margin: 0 }}>{t('empty.noDevices')}</h2>
          <p className="text-secondary" style={{ margin: 0 }}>
            {t('empty.noDevicesHint')}
          </p>
          <div className="dv-pairing-actions">
            <button className="btn-primary" onClick={() => setShowAdd((v) => !v)}>
              {t('devices.addCamera')}
            </button>
            <button className="btn-ghost" onClick={() => void reload()}>
              {t('common.refresh')}
            </button>
          </div>
        </div>
      )}

      {!error && devices && devices.length > 0 && (
        <>
          {groupByRoom(devices, rooms).map(({ room, label, devices: members }) => (
            <section key={room} data-testid={`room-${room}`}>
              <h2 className="dv-room-title">
                {label}
                <span className="text-secondary" style={{ fontSize: 'var(--font-size-sm)', marginLeft: 8 }}>
                  {t('devices.deviceCount', { count: members.length })}
                </span>
              </h2>
              {/* APP#39：房間內再依 device_class 分組(攝影機 / 智能裝置);單一類別時不顯示子標題。
                  APP#51:排序含自訂 order,分組後每組套卡片密度 + 拖曳排序 props。 */}
              {groupDevicesByClass(sortDevices(members, pins, sort, order)).map((grp, _gi, arr) => (
                <div key={grp.kind}>
                  {arr.length > 1 && (
                    <h3 className="dv-class-title" data-testid={`class-${room}-${grp.kind}`}>
                      {grp.kind === 'camera' ? t('devices.classCamera') : t('devices.classSmartIot')}
                    </h3>
                  )}
                  <div className={`dv-grid${density === 'compact' ? ' dv-compact' : ''}`}>
                    {grp.devices.map((d) => (
                      <div key={d.device_id} style={{ display: 'contents' }}>
                        <DeviceTile
                          d={d}
                          pinned={pins.includes(d.device_id)}
                          onTogglePin={onTogglePin}
                          quota={quotaByDevice.get(d.device_id)}
                          onExplain={(id) => setSheetDeviceId(id)}
                          reorderMode={reorderMode}
                          onReorderDrop={onReorderDrop}
                          selectMode={selectMode}
                          selected={selected.has(d.device_id)}
                          onToggleSelect={toggleSelect}
                        />
                        {editRooms && (
                          <div className="dv-room-edit" data-testid={`room-edit-${d.device_id}`}>
                            <input
                              list="dv-room-options"
                              defaultValue={rooms[d.device_id] ?? ''}
                              placeholder={t('devices.roomNamePlaceholder')}
                              onBlur={(e) => setRooms(saveRoom(uid, d.device_id, e.target.value))}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') setRooms(saveRoom(uid, d.device_id, (e.target as HTMLInputElement).value));
                              }}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </section>
          ))}
          <datalist id="dv-room-options">
            {roomNames.map((r) => (
              <option key={r} value={r} />
            ))}
          </datalist>
          <div className={`dv-grid${density === 'compact' ? ' dv-compact' : ''}`}>
          <button className="dv-tile dv-add" onClick={() => setShowAdd((v) => !v)}>
            <span className="dv-add-plus">＋</span>
            <span>{t('devices.addCamera')}</span>
            <span className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>
              {t('devices.addCameraHint')}
            </span>
          </button>
          </div>
        </>
      )}

      {showAdd && (
        <PairingPanel onClose={() => setShowAdd(false)} onBound={() => void reload()} />
      )}

      {sheetDeviceId && (
        <DegradedExplainSheet deviceId={sheetDeviceId} deviceLabel={sheetDevice ? deviceName(sheetDevice) : sheetDeviceId} onClose={() => setSheetDeviceId(null)} />
      )}
    </section>
  );
}
